import PublicNav from '../components/PublicNav';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';

const AGENTS = [
  {
    name: 'Spending Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Categorizes every transaction and compares month-over-month. Flags budget overruns before they snowball.',
    why: 'Most budgeting apps show you what happened. Agence tells you what it means.',
  },
  {
    name: 'Anomaly Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Detects unusual charges — large one-offs, duplicates, and repeated identical amounts. Threshold adapts to your actual spending history (mean + 2σ).',
    why: 'Catches fraud signals and billing errors a static $500 threshold would miss.',
  },
  {
    name: 'Goals Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Tracks pace toward each savings goal. Projects completion date based on current contribution rate.',
    why: 'Turns abstract targets into a live countdown with a clear verdict: on track or off.',
  },
  {
    name: 'Portfolio Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    source: 'Brokerage',
    what: 'Analyzes position concentration, unrealized P&L, and cash drag across all linked trading accounts simultaneously.',
    why: 'Surfaces the risks hiding inside a healthy-looking total return number.',
  },
  {
    name: 'Market Context Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    source: 'Brokerage + Finnhub',
    what: 'Pulls real-time prices and news sentiment for watchlisted tickers. Detects market regime (bullish/bearish/volatile) from SPY and QQQ.',
    why: 'Connects live market events to your actual positions — not a generic feed.',
  },
  {
    name: 'Watchlist Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
    source: 'Brokerage + Finnhub',
    what: 'Flags big movers (≥3% 24h change) and negative news sentiment on your tracked tickers.',
    why: 'Notifies you of significant moves in positions you\'re watching.',
  },
  {
    name: 'Autopilot Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
    source: 'Brokerage',
    what: 'Evaluates rule-based triggers against live market conditions and generates trade signals for your account, paper or live. Feeds the background automation scheduler.',
    why: 'Lets you test strategies on paper, or trade for real, without manual order entry — you confirm before anything executes.',
  },
  {
    name: 'Cross-Domain Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
    source: 'Plaid + Brokerage',
    what: 'Finds the intersection of your spending and your investments — flags when you spend heavily with companies you don\'t own, or when your sector exposure doesn\'t match your actual behavior.',
    why: 'The signal no other tool surfaces: your wallet and your portfolio are often misaligned.',
  },
  {
    name: 'Recurring Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
        <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Identifies active subscriptions and recurring charges via Plaid\'s recurring stream detection. Warns you when a charge is due within 3 days.',
    why: 'Prevents the surprise of a forgotten annual renewal draining your account.',
  },
  {
    name: 'Budget Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Compares current-month spending per category against your set budgets. Alerts at 80% and flags overruns.',
    why: 'Turns passive budget numbers into live warnings before month-end regret.',
  },
  {
    name: 'Debt Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>
    ),
    source: 'Plaid',
    what: 'Detects credit card balances carrying interest at ~22% APR. Surfaces paydown priority based on balance size.',
    why: 'Paying down high-interest debt at ~22% APR is among the highest-certainty ways to reduce costs — most people never quantify what it\'s actually costing them.',
  },
  {
    name: 'Tax Agent',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
    source: 'Brokerage',
    what: 'Identifies tax-loss harvesting opportunities and large unrealized gains. Uses FIFO lot-matching on brokerage fill history to determine short- vs. long-term holding periods.',
    why: 'Tax-loss harvesting can reduce your tax bill. Most people never look.',
  },
];

const STEPS = [
  ['1. Connect', 'Link your bank via Plaid and your own brokerage account. Data stays in your account — never sold or shared.'],
  ['2. Agents run in parallel', '12 agents fire simultaneously via Promise.all. The full analysis takes as long as the slowest single agent, not their sum.'],
  ['3. Insights stream live', 'Claude (claude-sonnet-4-6) acts as judge — receiving all 12 agents\' raw output and streaming ranked insights one by one as it reasons, so you see results within seconds.'],
  ['4. Autopilot runs on schedule', 'Enabled automations run on a background scheduler every 60 seconds during market hours — scanning up to 503 tickers per run and flagging a trade when conditions are met, no page load required. You confirm before anything executes.'],
  ['5. You review and decide', 'Dismiss or snooze any insight. Click a card to jump directly to the transaction, goal, or position that triggered it. Ask the AI chat assistant anything.'],
];

const STATS = [
  { value: '12', label: 'AI agents running in parallel' },
  { value: '3', label: 'financial data sources (banking · brokerage · market data)' },
  { value: '503', label: 'tickers scannable per automation run (S&P 500)' },
  { value: '60s', label: 'background scheduler interval during market hours' },
];

const NAV_GUIDE = [
  { path: 'Dashboard', desc: 'Portfolio equity hero, account selector, balances table, and your top AI insight. The one screen that tells you if today needs attention.' },
  { path: 'Insights', desc: 'Ranked feed of everything the agents found — streamed live, sorted by severity. Dismiss or snooze any card. Click to deep-link to the source.' },
  { path: 'Money → Expenses', desc: 'Full transaction history with category bars, period filters, and CSV export.' },
  { path: 'Money → Budgets', desc: 'Set monthly spending limits per category. Live progress bars update as transactions come in.' },
  { path: 'Money → Goals', desc: 'Create savings goals, track progress bars, and drag to reprioritize.' },
  { path: 'Markets → Portfolio', desc: 'Live positions, P&L, and trade history across all linked accounts. Place orders directly from this page, on paper or live accounts.' },
  { path: 'Markets → Watchlist', desc: 'Add tickers to track. Real-time prices, 24h change, and news sentiment per symbol.' },
  { path: 'Markets → Automations', desc: 'Configure AI autopilot or custom rule-based strategies. Evaluated on a schedule — no page load needed — but you confirm each trade before it executes. Performance stats and sparklines per automation.' },
  { path: 'Chat', desc: 'Ask anything: portfolio questions, transaction lookups, create automations by description, or get strategy advice with full context of your financial picture.' },
  { path: 'Account', desc: 'Connect bank accounts via Plaid, link your brokerage trading accounts, set budgets, manage household sharing.' },
];

export default function About() {
  return (
    <div className="page">
      <style>{`
        @media (max-width: 640px) {
          .about-step-row { flex-wrap: wrap !important; }
          .about-step-label { min-width: unset !important; width: 100%; }
          .about-nav-row { flex-wrap: wrap !important; }
          .about-nav-label { min-width: unset !important; width: 100%; }
          .about-stats { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
      <PageMeta title="About" description="Agence deploys 12 parallel AI agents across your spending, budgets, goals, and portfolio — the AI personal finance tool that connects your bank and brokerage." path="/about" schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'About Agence',
        'url': 'https://agence.finance/about',
        'description': '12 parallel AI agents analyze your spending, investments, and goals together and surface what actually needs your attention.',
        'about': {
          '@type': 'SoftwareApplication',
          'name': 'Agence',
          'applicationCategory': 'FinanceApplication',
          'operatingSystem': 'Web',
          'url': 'https://agence.finance',
        },
        'publisher': { '@type': 'Organization', 'name': 'Agence', 'url': 'https://agence.finance' },
      }} />
      <PublicNav />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 2.5rem 4rem' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.4rem, 6vw, 3.6rem)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
            lineHeight: 1.15,
          }}>
            Agents + Finance = Agence
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.7,
          }}>
            12 AI agents analyze your complete financial picture simultaneously.
            A judge synthesizes their outputs into one ranked, actionable insight feed —
            streamed live as it reasons.
          </p>
        </div>

        {/* Stats bar */}
        <section style={{ marginBottom: '3rem' }}>
          <div className="about-stats" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
          }}>
            {STATS.map(({ value, label }) => (
              <div key={label} style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--navy-200)',
                borderRadius: 'var(--radius-md)',
                padding: '1.25rem 1rem',
                textAlign: 'center',
              }}>
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: 'var(--navy-700)',
                  lineHeight: 1,
                  marginBottom: '0.4rem',
                }}>{value}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Where Agence is today */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 className="section-heading">Where Agence is today</h2>
          <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            Every trade — paper or live — requires your confirmation before it executes; nothing runs unattended.
            Here&apos;s what works today.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
            <div className="insight-card" style={{ padding: '1.25rem 1.5rem', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
              <p style={{ margin: '0 0 0.6rem', fontWeight: 700, color: 'var(--gain)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 'none' }}>Working now</p>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '0.92rem' }}>
                <li>Bank sync via Plaid</li>
                <li>12-agent AI insight feed</li>
                <li>Budgets, goals &amp; expenses</li>
                <li>Trading + autopilot (paper and live, via your connected brokerage)</li>
                <li>AI chat with memory</li>
                <li>Household sharing</li>
              </ul>
            </div>
          </div>
        </section>

        {/* The problem */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 className="section-heading">The Problem</h2>
          <div className="insight-card" style={{ padding: '1.5rem 1.75rem', flexDirection: 'column', alignItems: 'flex-start' }}>
            <p style={{ margin: 0, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              Robinhood and Rocket Money both exist — but they don't talk to each other.
              Your spending behavior and your investment behavior are deeply connected,
              yet no mainstream product surfaces that connection. Agence does.
              When your dining spend spikes the same month your portfolio dips,
              Agence sees both and tells you. When you spend heavily with a company you don't own stock in,
              Agence flags the misalignment.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 className="section-heading">How It Works</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {STEPS.map(([step, desc]) => (
              <div key={step} className="about-step-row" style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start',
                background: 'var(--bg-surface)',
                border: '1px solid var(--navy-200)',
                borderRadius: 'var(--radius-md)',
                padding: '1rem 1.25rem',
              }}>
                <span className="about-step-label" style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: 'var(--navy-600)',
                  minWidth: 220,
                  flexShrink: 0,
                  paddingTop: 2,
                }}>{step}</span>
                <span style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>{desc}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Agent cards */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 className="section-heading">The 12 Agents</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
            gap: '1rem',
          }}>
            {AGENTS.map(agent => (
              <div key={agent.name} className="insight-card" style={{ padding: '1.25rem 1.4rem', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <span style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: 'var(--navy-100)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>{agent.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{agent.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>via {agent.source}</div>
                  </div>
                </div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {agent.what}
                </p>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--navy-400)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {agent.why}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Nav guide */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 className="section-heading">Navigating the App</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {NAV_GUIDE.map(({ path, desc }) => (
              <div key={path} className="about-nav-row" style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start',
                padding: '0.75rem 1rem',
                background: 'var(--bg-surface)',
                border: '1px solid var(--navy-200)',
                borderRadius: 'var(--radius-sm)',
              }}>
                <code className="about-nav-label" style={{
                  fontSize: '0.8rem',
                  background: 'var(--navy-100)',
                  color: 'var(--navy-600)',
                  padding: '0.15rem 0.5rem',
                  borderRadius: 6,
                  whiteSpace: 'nowrap',
                  minWidth: 220,
                  display: 'inline-block',
                }}>{path}</code>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.6 }}>{desc}</span>
              </div>
            ))}
          </div>
        </section>


      </main>
      <Footer />
    </div>
  );
}
